import { useState, useEffect, useRef, useCallback } from "react";
import { API, authFetch, useAuth } from "@/App";
import { useSocketIo } from "@/hooks/useSocketIo";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FactoryChatPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState([]);
  const [newThreadUser, setNewThreadUser] = useState("");
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/chat/threads`);
      const data = await res.json();
      if (data.success) setThreads(data.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (threadId) => {
    try {
      const res = await authFetch(`${API}/factory/chat/${threadId}/messages`);
      const data = await res.json();
      if (data.success) setMessages(data.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);
  useEffect(() => {
    if (activeThread) {
      fetchMessages(activeThread.thread_id);
      const interval = setInterval(() => fetchMessages(activeThread.thread_id), 5000);
      return () => clearInterval(interval);
    }
  }, [activeThread, fetchMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Socket.IO replaces /ws/factory (P1 cleanup, iter 75). notify_factory mirrors
  // every CHAT_MESSAGE / NEW_DISPATCH / DISPATCH_UPDATED event to the SIO
  // `factory_event` channel in rooms role_FACTORY + role_SUPER_ADMIN.
  useSocketIo({
    factory_event: (msg) => {
      if (msg?.type === "CHAT_MESSAGE" && msg.data?.thread_id === activeThread?.thread_id) {
        setMessages((prev) => [...prev, msg.data]);
      }
      fetchThreads();
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await authFetch(`${API}/admin/users`);
      const data = await res.json();
      if (data.success) setUsers(data.data.filter(u => (u.role === "FACTORY_ADMIN" || u.role === "SUPER_ADMIN") && u.user_id !== user?.user_id));
    } catch { /* ignore */ }
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !activeThread) return;
    try {
      await authFetch(`${API}/factory/chat/${activeThread.thread_id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: msgText }),
      });
      setMsgText("");
      fetchMessages(activeThread.thread_id);
      fetchThreads();
    } catch { toast.error("Failed to send"); }
  };

  const createThread = async () => {
    if (!newThreadUser) return;
    try {
      const target = users.find(u => u.user_id === newThreadUser);
      const res = await authFetch(`${API}/factory/chat/threads`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "direct", name: target?.name || "", participants: [user.user_id, newThreadUser] }),
      });
      const data = await res.json();
      if (data.success) {
        setShowNew(false);
        setNewThreadUser("");
        fetchThreads();
        setActiveThread(data.data);
      }
    } catch { toast.error("Failed"); }
  };

  const getThreadName = (t) => {
    if (t.name) return t.name;
    if (t.participants?.length === 2) {
      const otherId = t.participants.find(p => p !== user?.user_id);
      const other = users.find(u => u.user_id === otherId);
      return other?.name || "Chat";
    }
    return "Group Chat";
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4" data-testid="factory-chat-page">
      {/* Thread List */}
      <div className="w-72 shrink-0 flex flex-col bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-hidden">
        <div className="p-3 border-b border-emerald-900/30 flex items-center justify-between">
          <h2 className="text-white font-semibold text-sm">Messages</h2>
          <Button size="sm" onClick={() => { setShowNew(true); fetchUsers(); }} className="bg-emerald-600 text-white h-7 text-xs" data-testid="new-chat-btn"><Plus className="w-3 h-3 mr-1" /> New</Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></div> :
            threads.length === 0 ? <p className="text-center text-slate-500 text-xs py-8">No conversations yet</p> :
            threads.map(t => (
              <button key={t.thread_id} onClick={() => setActiveThread(t)}
                className={`w-full text-left p-3 border-b border-emerald-900/20 hover:bg-emerald-900/10 transition-colors ${activeThread?.thread_id === t.thread_id ? "bg-emerald-900/20" : ""}`}
                data-testid={`thread-${t.thread_id}`}>
                <p className="text-white text-sm font-medium truncate">{getThreadName(t)}</p>
                <p className="text-slate-500 text-xs truncate mt-0.5">{t.last_message || "No messages"}</p>
              </button>
            ))
          }
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-[#1C2B2B] border border-emerald-900/30 rounded-xl overflow-hidden">
        {activeThread ? (
          <>
            <div className="p-3 border-b border-emerald-900/30">
              <h3 className="text-white font-medium">{getThreadName(activeThread)}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => {
                const isMine = m.sender_id === user?.user_id;
                return (
                  <div key={m.message_id || i} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${i}`}>
                    <div className={`max-w-[70%] rounded-xl px-3 py-2 ${isMine ? "bg-emerald-600 text-white" : "bg-[#0F1A1A] text-slate-200"}`}>
                      {!isMine && <p className="text-[10px] text-emerald-400 font-medium mb-0.5">{m.sender_name}</p>}
                      <p className="text-sm">{m.text}</p>
                      <p className={`text-[9px] mt-1 ${isMine ? "text-emerald-200" : "text-slate-500"}`}>
                        {new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-emerald-900/30 flex gap-2">
              <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Type a message..."
                className="bg-[#0F1A1A] border-emerald-900/50 text-white" data-testid="chat-input"
                onKeyDown={(e) => e.key === "Enter" && sendMessage()} />
              <Button onClick={sendMessage} className="bg-emerald-600 text-white" data-testid="send-msg-btn"><Send className="w-4 h-4" /></Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-emerald-700 mx-auto mb-3" />
              <p className="text-slate-400">Select a conversation or start a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* New Thread Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader><DialogTitle className="text-white">New Conversation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-emerald-600 font-medium">Select Person</label>
              <Select value={newThreadUser} onValueChange={setNewThreadUser}>
                <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="select-chat-user"><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role}) {u.assigned_unit ? `- ${u.assigned_unit}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={createThread} className="w-full bg-emerald-600 text-white" data-testid="create-thread-btn">Start Chat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FactoryChatPage;
